import bootstrap5Plugin from '@fullcalendar/bootstrap5';
import { EventClickArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import SquareIcon from '@mui/icons-material/Square';
import { Box, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { addDays } from 'date-fns';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    useGetIndividualContractorQuery,
    useGetJobsQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import '../../../../layout/styledcomponents/FullCalendar.css';
import UserRole from '../../../../shared/AuthRoles';
import { dayToNumber } from '../../../../shared/utils';
import { Period } from '../../../types/contractor';
import ContractorJobPreview from '../Jobs/ContractorJobPreview';

export type Status =
    | 'pending'
    | 'accepted'
    | 'declined'
    | 'cancelled'
    | 'completed'
    | 'pendingCompletion'
    | undefined;

interface JobListProps {
    status?: Status;
    bookingId?: number;
}

const CalendarList = ({ status, bookingId }: JobListProps) => {
    const theme = useTheme();
    const loggedInUser = UserRole();

    const { data: contractor } = useGetIndividualContractorQuery(
        loggedInUser?.company,
        { skip: !loggedInUser?.company },
    );

    const { data: jobs } = useGetJobsQuery({
        bookingId,
        ...(status ? { status } : {}),
    });
    const calendarRef: any = useRef(null);
    const isMobile = useMediaQuery('(max-width: 770px)');

    const [selectedJob, setSelectedJob] = useState<any>(null);
    const [jobOpen, setJobOpen] = useState(false);

    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        const id = searchParams.get('selectedJob');

        if (id) {
            setSelectedJob(parseInt(id));
            setJobOpen(true);
        }
    }, [searchParams]);

    const handleView = (clickInfo: EventClickArg) => {
        setSelectedJob(clickInfo?.event?._def.publicId);
        setJobOpen(true);

        setSearchParams(`selectedJob=${clickInfo?.event?._def.publicId}`);
    };

    const handleClose = () => {
        setJobOpen(false);
        setSelectedJob(null);

        setSearchParams();
    };

    const getDates = jobs
        ?.filter(
            (value: any) =>
                value?.status !== 'cancelled' && value?.status !== 'declined',
        )
        ?.map((value: any) => {
            let endDate = value?.jobDate;
            if (value?.jobDate === value?.jobDate) {
                endDate = value?.jobDate;
            } else {
                endDate = moment(addDays(new Date(value?.jobDate), 1)).format(
                    'YYYY-MM-DD',
                );
            }

            return {
                owner: value?.owner.name,
                id: value?.id,
                title: `Job #${value?.id} - Client: ${value?.owner?.name} - (${
                    Array.isArray(value?.services)
                        ? value.services
                              .map((service: string) =>
                                  service
                                      .replace(/([a-z])([A-Z])/g, '$1 $2')
                                      .replace(/^./, (str: string) =>
                                          str.toUpperCase(),
                                      ),
                              )
                              .join(', ')
                        : ''
                }) Status: ${value?.status
                    .replace(/([a-z])([A-Z])/g, '$1 $2')
                    .replace(/^./, (str: string) => str.toUpperCase())}`,

                start: value?.jobDate,
                extendedProps: {
                    status: value?.status,
                    moduleId: value?.module.id,
                },
                end: endDate,
                backgroundColor:
                    value?.status === 'completed'
                        ? theme.palette.midGrey.main
                        : value?.status === 'pending'
                          ? theme.palette.neutral.main
                          : value?.status === 'pendingCompletion'
                            ? theme.palette.yellow.main
                            : theme.palette.textAccent.main,
                textColor:
                    value?.status === 'pending'
                        ? theme.palette.midGrey.main
                        : theme.palette.neutral.main,
                borderColor:
                    value?.status === 'pending'
                        ? theme.palette.midGrey.main
                        : theme.palette.neutral.main,
                // borderColor: '#000'CCCCCC
            };
        });

    const unavailableDates =
        contractor?.unavailablePeriods?.map((p: Period) => ({
            id: `${p.from}-${p.to}`,
            start: moment(p.from).format('YYYY-MM-DD'),
            end: moment(p.to).add(1, 'day').format('YYYY-MM-DD'),
            allDay: true,
            display: 'background',
            backgroundColor: theme.palette.custom.grey,
            overlap: false,
            editable: false,
            selectable: false,
        })) ?? [];

    const availableDays = (
        contractor?.availableDays.map((availability) => availability.day) || []
    ).map((day) => dayToNumber[day]);

    return (
        <>
            <Box
                component='section'
                aria-label='Calendar'
                sx={{
                    height: isMobile ? 550 : 800,
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    paddingTop: '50px',
                    // display: 'flex',
                    justifyContent: isMobile ? 'center' : 'left', // Centering the Box content
                    alignItems: isMobile ? 'center' : 'left', // Ensuring content is vertically centered
                }}
            >
                <FullCalendar
                    themeSystem='bootstrap5'
                    ref={calendarRef}
                    firstDay={1}
                    plugins={[
                        dayGridPlugin,
                        timeGridPlugin,
                        listPlugin,
                        bootstrap5Plugin,
                        interactionPlugin,
                    ]}
                    businessHours={{
                        daysOfWeek: availableDays,
                    }}
                    initialView={isMobile ? 'timeGridDay' : 'dayGridMonth'}
                    events={[...(getDates || []), ...unavailableDates]}
                    editable={true}
                    selectable={true}
                    selectMirror={true}
                    eventClick={handleView}
                    height={'100%'}
                    headerToolbar={{
                        start: isMobile
                            ? 'title'
                            : 'timeGridDay,timeGridWeek,dayGridMonth,listWeek',
                        center: isMobile ? '' : 'title',
                        end: isMobile
                            ? ''
                            : 'prevYear,prev,today,next,nextYear',
                    }}
                    footerToolbar={{
                        start: isMobile ? '' : '',
                        center: isMobile
                            ? 'prevYear,prev,today,next,nextYear'
                            : '',
                        end: isMobile
                            ? 'timeGridDay,timeGridWeek,dayGridMonth,listWeek'
                            : '',
                    }}
                    buttonText={{
                        today: 'Today',
                        month: 'Month',
                        week: 'Week',
                        day: 'Day',
                        list: 'List',
                    }}
                    eventDidMount={function (info: any) {
                        if (info.event.extendedProps.status === 'pending') {
                            info.el.style.color = theme.palette.textAccent.main;

                            // Change color of dot marker
                            const titleEl = info.el.getElementsByClassName(
                                'fc-list-event-title',
                            )[0];
                            const dotEl =
                                info.el.getElementsByClassName(
                                    'fc-list-event-dot',
                                )[0];
                            if (titleEl) {
                                titleEl.style.color =
                                    theme.palette.textAccent.main;
                                dotEl.style.borderColor =
                                    theme.palette.gray.main;
                            }
                        } else if (
                            info.event.extendedProps.status === 'completed'
                        ) {
                            info.el.style.color = theme.palette.textAccent.main;

                            // Change color of dot marker
                            const titleEl = info.el.getElementsByClassName(
                                'fc-list-event-title',
                            )[0];
                            const dotEl =
                                info.el.getElementsByClassName(
                                    'fc-list-event-dot',
                                )[0];
                            if (titleEl) {
                                titleEl.style.color =
                                    theme.palette.textAccent.main;
                                dotEl.style.borderColor =
                                    theme.palette.gray.main;
                            }
                        } else {
                            info.el.style.color = theme.palette.textAccent.main;

                            // Change color of dot marker if approved
                            const titleEl = info.el.getElementsByClassName(
                                'fc-list-event-title',
                            )[0];
                            const dotEl =
                                info.el.getElementsByClassName(
                                    'fc-list-event-dot',
                                )[0];
                            if (titleEl) {
                                titleEl.style.color =
                                    theme.palette.textAccent.main;
                                dotEl.style.borderColor =
                                    theme.palette.info.main;
                            }
                        }
                    }}

                    // customButtons={{
                    //   custom1: {
                    //     text: "custom 1",
                    //     click: function () {
                    //       alert("clicked the custom button!");
                    //     },
                    //   },
                    // }}
                />
                <Grid
                    sx={{
                        width: 'auto',
                        paddingTop: '20px',
                        paddingBottom: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Grid>
                        <Typography
                            sx={{
                                fontSize: '12px',
                                paddingRight: '5px',
                                textAlign: 'left',
                                paddingLeft: isMobile ? '15px' : '0px',
                            }}
                        >
                            Note: The Calendar does not include cancelled or
                            declined jobs.
                        </Typography>
                    </Grid>

                    <Grid sx={{ width: '100%' }}>
                        <Grid
                            sx={{
                                display: 'flex',
                                flexDirection: isMobile ? 'column' : 'row',
                                paddingLeft: isMobile ? '15px' : '0px',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Grid
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '10px',
                                }}
                            >
                                {[
                                    {
                                        color: theme.palette.textAccent.main,
                                        text: 'Upcoming',
                                    },
                                    {
                                        color: theme.palette.neutral.main,
                                        text: 'Pending',
                                        borderColor: theme.palette.midGrey.main,
                                    },

                                    {
                                        color: theme.palette.midGrey.main,
                                        text: 'Completed',
                                        Icon: CropSquareIcon,
                                    },
                                    {
                                        color: theme.palette.yellow.main,
                                        text: 'Pending Completion ',
                                        Icon: CropSquareIcon,
                                    },
                                ].map(({ color, text, borderColor }, idx) => (
                                    <Grid
                                        key={idx}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <SquareIcon
                                            fontSize='small'
                                            sx={{
                                                fontSize: '16px',
                                                color,
                                                paddingRight: '3px',
                                                border: borderColor
                                                    ? `1px solid ${borderColor}`
                                                    : 'none',
                                                borderRadius: '2px', // optional for slight rounding
                                                backgroundColor: color,
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                fontSize: '12px',
                                                paddingLeft: '5px',
                                                paddingRight: '5px',
                                            }}
                                        >
                                            {text}
                                        </Typography>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>

            <ContractorJobPreview
                id={selectedJob}
                open={jobOpen}
                onClose={handleClose}
            />
        </>
    );
};

export default CalendarList;
